/* eslint-disable @next/next/no-img-element */
import cn from 'classnames';
import copy from 'copy-to-clipboard';
import { API_STATUS } from 'core-lib/constants/network';
import { Source, VENDOR_ID_SKU } from 'core-lib/constants/sourceSKU';
import { useProfile } from 'core-lib/hooks/useProfile';
import fetchPromoList from 'payments-ui-v2/services/fetchPromoList';
import { PromoItem } from 'payments-ui-v2/types/Promo';
import React, { useEffect, useMemo, useState } from 'react';
import useParkplusStoreV2 from 'src/modules/parkplusStoreV2/hooks/useParkplusStoreV2';
import { ProductDescription } from 'src/modules/parkplusStoreV2/types/product';
import { Divider } from 'ui-v2/components/Divider';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import { Typography } from 'ui-v2/components/Typography';
import s from './Offers.module.scss';
import { customToast } from 'core-lib/utils/customToast';
import { trackCtaClickedStoreV2 } from 'src/modules/parkplusStoreV2/utils/events';
import { EXTENDED_LOADING_STATES } from 'src/modules/parkplusStoreV2/constants';
import Icon from 'ui-v2/components/Icon/Icon';
import classNames from 'classnames';

type OffersProps = {
  data: ProductDescription;
};

export const Offers: React.FunctionComponent<OffersProps> = ({ data }) => {
  // state
  const [offers, setOffers] = useState<PromoItem[]>([]);
  const [offerApiStatus, setOfferApiStatus] = useState(API_STATUS.default);
  const [selectedCode, setSelectedCode] = useState('');

  // custom hooks
  const { isAuthenticated } = useProfile();
  const { cartData } = useParkplusStoreV2();

  // constants
  const cartItem = useMemo(
    () => cartData?.data?.find((item) => item?.productId === data?.productId),
    [cartData, data]
  );

  const fetchProductOffers = async () => {
    setOfferApiStatus(API_STATUS.in_progress);
    const body = {
      price: data?.price,
      vendor_id: VENDOR_ID_SKU[Source.ParkplusStore]?.vendorId,
      item_details: [
        {
          sku: data?.productSku,
          quantity: cartItem?.quantity ?? 1,
          unit_price: data?.price,
          category_id: VENDOR_ID_SKU[Source.ParkplusStore]?.vendorId,
          sub_category_id: data?.subCategoryId,
          item_type: 'master',
        },
      ],
    };

    const formattedData = await fetchPromoList(body);

    if (formattedData?.response) {
      setOffers(formattedData.response);
      setOfferApiStatus(API_STATUS.success);
    } else {
      setOfferApiStatus(API_STATUS.failure);
    }
  };

  const copyToClipboard = (code: string) => {
    copy(code);
    setSelectedCode(code);
    customToast({
      variant: 'positiveNotPriority',
      label: `Promo ${code} copied to clipboard!`,
    });
    trackCtaClickedStoreV2('offers', code);
  };
  useEffect(() => {
    if (data?.productSku && isAuthenticated) {
      fetchProductOffers();
    }
  }, [data, isAuthenticated]);

  if (offers?.length <= 0) {
    return null;
  }

  if (EXTENDED_LOADING_STATES.includes(offerApiStatus)) {
    return (
      <div className={s.shimmer_container}>
        <div className={cn('cmn_shimmer', s.shimmer_header)} />

        <div className={s.shimmer_block_container}>
          <div className={s.shimmer_block}>
            <div className={cn('cmn_shimmer', s.code)} />
            <div className={cn('cmn_shimmer', s.title)} />
            <div className={cn('cmn_shimmer', s.subtitle)} />
          </div>

          <div className={s.half_shimmer_block}>
            <div className={s.code} />
            <div className={s.title} />
            <div className={s.subtitle} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.offersContainer}>
      <div className={s.offers}>
        {/* <div className={s.offerIcon}>
          <img src="/app/img/parkplus-store/offers.gif" alt="" />
        </div> */}
        <Typography
          label={`Offers you shouldn’t miss (${offers.length})`}
          variant="label_2_bold"
        />
      </div>

      <div className={cn(s.offerDetailContainer, 'cmn_hide_scrollbar')}>
        {offers.map((offer: PromoItem) => (
          <div className={s.offerDetail} key={offer?.code}>
            <div className={s.details}>
              <div className={s.icon}>
                <AnimatedImage src={offer?.image} alt="promoIcon" />
              </div>
              <div className={s.code}>
                <div className={s.code_text}>
                  <Typography
                    label={`Code: ${offer.code}`}
                    variant="label_3_semi_bold"
                  />
                </div>
              </div>
              <div className={s.sub_description}>
                <div className={s.description}>
                  <Typography
                    label={offer?.description}
                    variant="body_4_regular"
                    color="tertiary"
                  />
                </div>
                {selectedCode === offer?.code ? (
                  <div
                    className={s.copied_success}
                    onClick={() => copyToClipboard(offer.code)}
                  >
                    <Icon name="CopyBold" />
                  </div>
                ) : (
                  <div
                    className={s.copyIcon}
                    onClick={() => copyToClipboard(offer.code)}
                  >
                    <Icon name="CopyRegular" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Divider variant="padded" />
    </div>
  );
};
